import { useEffect, useState } from "react";
import rothko from "web/script/modules/rothko";
import { URLQuery } from "web/script/utils/url";

export enum LOADING_STATES {
    DEFAULT,
    PENDING,
    COMPLETE,
    ERROR,
}

/*  Note - parameters needs to be a reference to an object between re-renders as useEffect does not do a deep object comparison when deciding
if it should execute. For example:
```
const [rothkoOptions] = useMemo(() =>({ slug: productSlug, uid: productUid }), [productSlug, productUid]);
const { data, loadingState } = useRothko(endpoint, rothkoOptions);
```
This will ensure parameters is always a reference to the same object
*/
export function useRothko<T = any>(
    endpoint: string,
    parameters?: URLQuery
): { data: T | undefined; loadingState: LOADING_STATES } {
    const [data, updateData] = useState<T | undefined>(undefined);
    const [loadingState, setLoadingState] = useState(LOADING_STATES.DEFAULT);

    useEffect(() => {
        async function fetchData(): Promise<void> {
            setLoadingState(LOADING_STATES.PENDING);
            try {
                const response = await rothko.fetch<T>(endpoint, parameters);
                updateData(response);
                setLoadingState(LOADING_STATES.COMPLETE);
            } catch (err) {
                setLoadingState(LOADING_STATES.ERROR);
            }
        }
        fetchData();
    }, [endpoint, parameters]);

    return { data, loadingState };
}
